/**
 * WriteoffSlice Approvals Reducers
 * 
 * Reducers for managing writeoff approvals.
 */

import { PayloadAction, CaseReducer } from '@reduxjs/toolkit';
import { WriteoffApprovalView } from '../../types';
import { WriteoffSliceState } from '../state';
import { addEditedField, createBackupManager } from '../../../shared/sliceUtils';

// Backup manager for HMR recovery
const backupManager = createBackupManager<{
  writeoff: WriteoffSliceState['currentWriteoff'];
  isEdited: boolean;
  editedFields: string[];
}>('writeoff_backup');

/**
 * Helper to persist state to session storage
 */
function persistToSessionStorage(state: WriteoffSliceState) {
  if (state.currentWriteoff) {
    backupManager.persist({
      writeoff: state.currentWriteoff,
      isEdited: state.isEdited,
      editedFields: state.editedFields,
    });
  }
}

// =============================================================================
// APPROVALS REDUCERS
// =============================================================================

/**
 * Add a new approval to the writeoff
 */
export const addApproval: CaseReducer<WriteoffSliceState, PayloadAction<WriteoffApprovalView>> = (
  state,
  action
) => {
  if (state.currentWriteoff) {
    state.currentWriteoff.approvals.push(action.payload);
    addEditedField(state, 'approvals');
    persistToSessionStorage(state);
  }
};

/**
 * Update an existing approval by level
 */
export const updateApproval: CaseReducer<
  WriteoffSliceState,
  PayloadAction<{ level: number; updates: Partial<WriteoffApprovalView> }>
> = (state, action) => {
  if (state.currentWriteoff) {
    const approvalIndex = state.currentWriteoff.approvals.findIndex(
      (approval) => approval.level === action.payload.level
    );
    if (approvalIndex !== -1) {
      state.currentWriteoff.approvals[approvalIndex] = {
        ...state.currentWriteoff.approvals[approvalIndex],
        ...action.payload.updates,
      };
      addEditedField(state, 'approvals');
      persistToSessionStorage(state);
    }
  }
};
